// frontend/src/components/ReviewPage/ComparisonSwitch.tsx
import { FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material'
import type { ComparisonMode, ComparisonState } from './useComparisonState'
import { CONTROLS } from '~/components/Charts/chrome/chartChrome'

interface Props {
  state: ComparisonState
  rootOptions: Array<{ value: string; label: string }>
  primaryRoot: string
  /** Row 21: roots ALREADY rendered on the page (e.g. the Dynamics driver row) — excluded
   *  from the DEFAULT metric-compare pick only; they stay selectable in the dropdown. */
  excludedDefaultRoots?: string[]
}

export function ComparisonSwitch({
  state,
  rootOptions,
  primaryRoot,
  excludedDefaultRoots = [],
}: Props) {
  const otherRoots = rootOptions.filter((o) => o.value !== primaryRoot)
  const metricAvailable = otherRoots.length >= 1 // i.e. the page exposes >=2 roots total

  const onModeChange = (next: ComparisonMode) => {
    if (next === 'metric') {
      // Row 21: default to the first root NOT already displayed; if every candidate is
      // displayed, fall back to the first (an imperfect default beats a disabled feature).
      const pick = otherRoots.find((o) => !excludedDefaultRoots.includes(o.value)) ?? otherRoots[0]!
      state.setMetricCompare(pick.value)
    } else state.setMode(next)
  }

  return (
    // flexWrap: inside the V7 Comparison ControlCard the 2nd-metric select must wrap
    // below the mode select instead of overflowing the card edge.
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="cmp-mode-label">{CONTROLS.compareWith}</InputLabel>
        <Select
          labelId="cmp-mode-label"
          label={CONTROLS.compareWith}
          value={state.mode}
          onChange={(e) => onModeChange(e.target.value as ComparisonMode)}
        >
          <MenuItem value="off">{CONTROLS.compareOff}</MenuItem>
          <MenuItem value="period">{CONTROLS.comparePreviousPeriod}</MenuItem>
          {metricAvailable ? (
            <MenuItem value="metric">{CONTROLS.compareOtherMetric}</MenuItem>
          ) : null}
        </Select>
      </FormControl>

      {state.mode === 'metric' ? (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="cmp-kpi-label">{CONTROLS.compareSecondMetric}</InputLabel>
          <Select
            labelId="cmp-kpi-label"
            label={CONTROLS.compareSecondMetric}
            value={otherRoots.some((o) => o.value === state.compareKpi) ? state.compareKpi : ''}
            onChange={(e) => state.setCompareKpi(e.target.value)}
          >
            {otherRoots.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Stack>
  )
}
